import React, { useState, useRef, useEffect, memo } from "react";
import PropTypes from "prop-types";
import { handleMouseEnter, handleMouseLeave, navigateToCell } from "./shared.js";

export const RenderSignOutEditable = memo(({
  row,
  rowCount,
  column,
  onCellInputBlur,
  viewOnly,
}) => {
  const initialValue = row.original.signOutComments || "";
  const [value, setValue] = useState(initialValue);
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const { index } = row;
  const { id } = column;
  const textareaRef = useRef(null);
  const hoverTimeoutRef = useRef(null);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    if (textareaRef.current) {
      if (isFocused || isHovered) {
        // Reset height to auto to recalculate based on content
        textareaRef.current.style.height = "auto";
        // Set height to match scrollHeight
        textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
      } else {
        // On blur or mouse out, return to the original height
        textareaRef.current.style.height = "85%";
      }
    }
  }, [value, isFocused, isHovered]);

  return (
    <textarea
      ref={textareaRef}
      id={`${id}-${index}`}
      style={{
        width: "100%",
        minWidth: "150px",
        height: "85%",
        overflow: "hidden", // Prevent scrollbar flickering
        resize: "none", // Disable manual resizing (optional)
        transition: isFocused ? "none" : "height 0.3s ease", // Smooth transition only when collapsing
        fontWeight: viewOnly ? "bold" : "normal", // Bold text when viewOnly
        backgroundColor: viewOnly ? "#F2F2F2" : "", // Keep background readable
        color: viewOnly ? "black" : "", // Ensure text is not grayed out
        cursor: viewOnly ? "not-allowed" : "text", // Show appropriate cursor
        boxSizing: "border-box",
      }}
      maxLength="4000"
      value={value}
      onChange={(e) => !viewOnly && setValue(e.currentTarget.value)}
      onFocus={() => !viewOnly && setIsFocused(true)}
      onBlur={(e) => {
        setIsFocused(false);
        const newValue = e.currentTarget.value;
        if (newValue !== initialValue) {
          onCellInputBlur(newValue, index, id);
        }
      }}
      onKeyDown={(e) => {
        const { selectionStart, selectionEnd } = e.target;
        const atStart = selectionStart === 0 && selectionEnd === 0;
        const atEnd = selectionStart === value.length && selectionEnd === value.length;

        if (e.key === "ArrowUp" || e.key === "ArrowDown") {
          e.preventDefault();
          navigateToCell(id, index, e.key, rowCount);
        } else if (e.key === "ArrowLeft" && atStart) {
          e.preventDefault();
          navigateToCell(id, index, e.key, rowCount);
        } else if (e.key === "ArrowRight" && atEnd) {
          e.preventDefault();
          navigateToCell(id, index, e.key, rowCount);
        }
      }}
      onMouseEnter={() => handleMouseEnter(hoverTimeoutRef, setIsHovered)}
      onMouseLeave={() => handleMouseLeave(hoverTimeoutRef, setIsHovered)}
    />
  );
});

RenderSignOutEditable.displayName = "RenderSignOutEditable";

RenderSignOutEditable.propTypes = {
  row: PropTypes.shape({
    index: PropTypes.number.isRequired,
    original: PropTypes.shape({
      signOutComments: PropTypes.string,
    }).isRequired,
  }).isRequired,
  rowCount: PropTypes.number.isRequired,
  column: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
  onCellInputBlur: PropTypes.func.isRequired,
  viewOnly: PropTypes.bool.isRequired,
};
